import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import DefaultLayout from "/github/workspace/node_modules/@adobe/gatsby-theme-aio/src/components/MDXFilter/index.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h1 {...{
      "id": "logging-and-monitoring"
    }}>{`Logging and Monitoring`}</h1>
    <p>{`The `}<inlineCode parentName="p">{`AIO`}</inlineCode>{` CLI offers a number of tools you can use to debug your actions while running them on Adobe I/O Runtime.`}</p>
    <p>{`You can retrieve the latest activations in a namespace by running:`}</p>
    <pre><code parentName="pre" {...{}}>{`aio rt:activation:list
`}</code></pre>
    <p>{`Having an activation ID, you can retrieve the activation result running:`}</p>
    <pre><code parentName="pre" {...{}}>{`aio rt:activation:get <activation ID>
`}</code></pre>
    <p>{`If you send data to logs from your actions (using `}<inlineCode parentName="p">{`console.log()`}</inlineCode>{` in your code), you`}{`’`}{`ll get this information as part of the activation record, inside the `}<inlineCode parentName="p">{`logs`}</inlineCode>{` field. The shortcut command to get the logs is:`}</p>
    <pre><code parentName="pre" {...{}}>{`aio rt:activation:logs <activation ID>
//output sample
2018-11-14T22:23:00.002Z       stdout: 1542234180001: param = John Doe
`}</code></pre>
    <h1 {...{
      "id": "retrieving-activations-for-blocking-successful-calls"
    }}>{`Retrieving Activations for Blocking Successful Calls`}</h1>
    <p>{`At scale, when you run millions of activations in a day, it may be difficult to extract the activations that failed in order to debug them. To help with this task, the system skips persisting the activation that succeeded. `}</p>
    <p>{`The exceptions are asynchronous actions that are invoked in a non-blocking fashion. Their results are persisted regardless
so that you can extract the response of the action at a later time. `}</p>
    <p>{`However, during development it is important to have access to all activation results. You can enable this by setting in the request the extra logging header to `}<inlineCode parentName="p">{`on`}</inlineCode>{`: `}</p>
    <pre><code parentName="pre" {...{}}>{`X-OW-EXTRA-LOGGING: on
`}</code></pre>
    <blockquote>
      <p parentName="blockquote">{`It's not recommended to use the extra logging headers in the production environment as invocation performance can be impacted. `}</p>
    </blockquote>
    <h2 {...{
      "id": "understanding-the-error-codes"
    }}>{`Understanding The Error Codes`}</h2>
    <p>{`When you have action invocations that fail, sometimes the best way to understand the reason behind the failing is to take a look at the error object part of the activation record - running `}<inlineCode parentName="p">{`aio rt:activation:get <activationId>`}</inlineCode>{` you will get the activation record.`}</p>
    <p>{`The following are the possible values and the meaning for `}<inlineCode parentName="p">{`error`}</inlineCode>{` in `}<inlineCode parentName="p">{`response.status`}</inlineCode>{`:`}</p>
    <ul>
      <li parentName="ul"><inlineCode parentName="li">{`success`}</inlineCode>{`: everything is okay (status is true)`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`action developer error`}</inlineCode>{`: A container or action code error occurred, e.g. failed to start action (status is false). This is usually the case if the nodejs action code has a syntax error or missing dependencies`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`application error`}</inlineCode>{`: Action ran, but there was an error thrown in the action code (deliberatly or not) that was handled by I/O Runtime (status is false).`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`whisk internal error`}</inlineCode>{`: Action did not run, an internal I/O Runtime system error occurred while starting the action, more info in `}<inlineCode parentName="li">{`response.status.result.error`}</inlineCode></li>
    </ul>
    <h2 {...{
      "id": "3rd-party-tools"
    }}>{`3rd-Party Tools`}</h2>
    <p>{`I/O Runtime doesn’t offer a configuration to send activations and logs to an external system, something like Splunk, Datadog or New Relic. This is something we are considering to offer in the future. `}</p>
    <p>{`Although there is no out-of-the-box integration, there are still ways you can push data from I/O Runtime to an external tool in order to monitor and debug your actions. `}</p>
    <h4 {...{
      "id": "epsagon"
    }}>{`Epsagon`}</h4>
    <p>{`One tool that made it easy to do this is `}<a parentName="p" {...{
        "href": "https://epsagon.com/?utm_source=adobe.io&utm_medium=referral&utm_campaign=adobe_io_docs"
      }}>{`Epsagon`}</a>{`. Epsagon built an integration for OpenWhisk based systems (I/O Runtime is built on top of the open source project OpenWhisk) that makes super easy to see your activations, errors, latency information and logs in their system. Check this `}<a parentName="p" {...{
        "href": "https://docs.epsagon.com/docs/openwhisk?utm_source=adobe.io&utm_medium=referral&utm_campaign=adobe_io_docs"
      }}>{`guide`}</a>{` or this `}<a parentName="p" {...{
        "href": "https://www.youtube.com/watch?v=4iprbivqrxQ&t=1517s"
      }}>{`video presentation`}</a>{` if you want to find more.`}</p>
    <h4 {...{
      "id": "new-relic"
    }}>{`New Relic`}</h4>
    <p>{`Another tool that can be used to monitor your actions is New Relic, which offers a Node.js agent that can be used to monitor your actions. Check this `}<a parentName="p" {...{
        "href": "https://docs.newrelic.com/docs/agents/nodejs-agent/getting-started/introduction-new-relic-nodejs"
      }}>{`guide`}</a>{` to learn more.`}</p>
    <p>{`For example if you want to send to New Relic the execution time for one action, you could build your action code as follows:`}</p>
    <pre><code parentName="pre" {...{}}>{`const newrelic = require('newrelic');

function main(params) {
    const start = Date.now();
    
    // your action code here
    
    const end = Date.now();
    const durationInMilliseconds = end - start;
    
    // send duration to New Relic
    newrelic.recordMetric('Custom/RunEndpointDuration', durationInMilliseconds);
}
`}</code></pre>
    <p>{`You need to package the new relic agent as part of your action code and deploy the action as `}<a parentName="p" {...{
        "href": "creating_actions#deploying-zip-actions"
      }}>{`.zip file`}</a>{`. `}</p>
    <h2 {...{
      "id": "debugging-locally"
    }}>{`Debugging Locally`}</h2>
    <p>{`Check this `}<a parentName="p" {...{
        "href": "debugging.md"
      }}>{`page`}</a>{` if you want to learn how to debug your actions locally.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      